/**
 * Wrap Monaco editor. See also:
 * import MonacoEditor from '@wandb/weave/common/components/Monaco/Editor';
 * This is a controlled component that automatically sets its height based on
 * the text content.
 */

import {THEME} from '@wandb/weave/common/css/color.styles';
import React, {useRef, useState} from 'react';

import {importWithRetry} from '../../util/dynamicImportRetry';
import {isComponentModule} from '../../util/lazyImport';
import {useColorMode} from '../../util/useColorMode';

// Each import is retried separately: retry re-imports the URL that failed and
// returns that module, so it cannot wrap a loader that imports twice.
const Editor = React.lazy(async () => {
  await importWithRetry(
    () => import('@wandb/weave/common/components/Monaco/bootstrap')
  );
  return await importWithRetry(() => import('@monaco-editor/react'), {
    isExpectedModule: isComponentModule,
  });
});

const BORDER_PX = 1;

type CodeEditorProps = {
  value: string;
  language?: string;
  readOnly?: boolean;
  wrapLines?: boolean;
  onChange?: (value: string) => void;
  maxHeight?: number;
  minHeight?: number;

  fillHeight?: boolean;

  // To enable horizontal scrolling with the mouse wheel and not just the scrollbar,
  // set handleMouseWheel to true and alwaysConsumeMouseWheel to false.
  handleMouseWheel?: boolean;
  alwaysConsumeMouseWheel?: boolean;
};

export const CodeEditor = ({
  value,
  language,
  readOnly,
  wrapLines,
  onChange,
  maxHeight,
  minHeight,
  fillHeight,
  handleMouseWheel,
  alwaysConsumeMouseWheel,
}: CodeEditorProps) => {
  const {isDarkMode} = useColorMode();
  const editorRef = useRef(null);
  const [height, setHeight] = useState(300);

  const updateHeight = fillHeight
    ? undefined
    : () => {
        const editor: any = editorRef.current;
        if (editor) {
          // box-sizing is border-box, so include border in height.
          const contentHeight = editor.getContentHeight() + 2 * BORDER_PX;
          const realMin = minHeight
            ? Math.max(minHeight, contentHeight)
            : contentHeight;
          setHeight(maxHeight ? Math.min(maxHeight, realMin) : realMin);
        }
      };

  const handleEditorBeforeMount = (monaco: any) => {
    monaco.editor.defineTheme('wandb-light', {
      base: 'vs',
      inherit: true,
      rules: [],
      colors: {
        'editorWidget.border': THEME.border,
      },
    });
  };

  const handleEditorDidMount = (editor: any, monaco: any) => {
    monaco.editor.setTheme(isDarkMode ? 'vs-dark' : 'wandb-light');
    editorRef.current = editor;
    if (updateHeight) {
      editor.onDidContentSizeChange(updateHeight);
      updateHeight();
    }
  };

  const width = '100%';
  const options = {
    readOnly,
    minimap: {
      enabled: false,
    },
    overviewRulerLanes: 0,
    scrollBeyondLastLine: false,
    scrollbar: {
      handleMouseWheel: handleMouseWheel ?? false,
      alwaysConsumeMouseWheel: alwaysConsumeMouseWheel ?? true,
    },
    wordWrap: wrapLines ? ('on' as const) : ('off' as const),
  };

  const onValueChange = (newValue: string | undefined) => {
    if (onChange) {
      onChange(newValue ?? '');
    }
  };

  return (
    <div
      style={{
        width,
        height: fillHeight ? '100%' : `${height}px`,
        border: `${BORDER_PX}px solid ${THEME.border}`,
      }}>
      <React.Suspense fallback={<></>}>
        <Editor
          height="100%"
          value={value}
          language={language}
          beforeMount={handleEditorBeforeMount}
          onMount={handleEditorDidMount}
          onChange={onValueChange}
          options={options}
          theme={isDarkMode ? 'vs-dark' : 'wandb-light'}
        />
      </React.Suspense>
    </div>
  );
};
